import { create } from 'zustand';
import {
  SPLIT_DEFAULT,
  applySplitDrag,
  identityControlPoints,
  transitionLockMode,
  computeHomography,
  leftSourceQuad,
  rightSourceQuad,
  type BezierPatch,
  type CoordUnit,
  type CornerIndex,
  type Corners,
  type CurrentMedia,
  type EditMode,
  type FineWarpPoint,
  type MaskPolygon,
  type WarpSlice,
  type Heatmap,
  type Matrix3x3,
  type MediaItem,
  type MediaTranscodeProgress,
  type NudgeStep,
  type PatternMode,
  type PlaybackSource,
  type Point,
  type RecentMedia,
  type SaveStatus,
  type Selection,
  type SplitLine,
  type SplitLockMode,
  type TestPattern,
  type WallSide,
  type WarpPreset,
} from '@cornerstone/shared';
import { api, type NewPresetInput } from '../client/api';

export type ConnectionStatus = 'connecting' | 'online' | 'offline';

/**
 * Surface template (NBL_REQ_309 / HNBL-65).
 *
 * Generalises the calibration geometry into selectable projection-surface
 * templates instead of the hard-coded left/right 2-wall + split + seam model:
 *
 *   - 'single' — one flat plane, no inner seam / no source split.
 *   - 'corner' — the legacy left/right 2-wall corner-projection layout
 *                (source split line + destination seam). This is the default
 *                so existing presets keep their current behaviour unchanged.
 *   - 'multi'  — multiple independent surfaces (P1, not yet implemented).
 *
 * This is a presentation-layer mode: it drives which calibration panels are
 * shown and does NOT mutate the persisted warp data model, so org-scoped
 * presets (warpPresetsRepo) and the Player renderer stay byte-compatible.
 */
export type SurfaceTemplate = 'single' | 'corner' | 'multi';

export type MediaChangePayload =
  | { kind: 'created'; item: MediaItem }
  | { kind: 'updated'; item: MediaItem }
  | { kind: 'deleted'; id: string }
  | { kind: 'played'; id: string };

// =============================================================================
// Calibration store — CALIBRATION_UX.md §6.1 / §6.7
// =============================================================================

/** TL↔TR (0↔1), BL↔BR (3↔2) */
const MIRROR_MAP: readonly [CornerIndex, CornerIndex, CornerIndex, CornerIndex] =
  [1, 0, 3, 2];

/** Destination-space mirror: x' = 1 - x, y' = y */
function mirrorPoint(p: Point): Point {
  return { x: 1 - p.x, y: p.y };
}

function replaceCorner(
  corners: Corners,
  index: CornerIndex,
  point: Point,
): Corners {
  const [a, b, c, d] = corners;
  const arr: [Point, Point, Point, Point] = [a, b, c, d];
  arr[index] = point;
  return arr;
}

const INITIAL_LEFT: Corners = [
  { x: 0, y: 0 },
  { x: 0.5, y: 0 },
  { x: 0.5, y: 1 },
  { x: 0, y: 1 },
];

const INITIAL_RIGHT: Corners = [
  { x: 0.5, y: 0 },
  { x: 1, y: 0 },
  { x: 1, y: 1 },
  { x: 0.5, y: 1 },
];

const MAX_HISTORY = 20;

// -----------------------------------------------------------------------------
// History entry & hashing
// -----------------------------------------------------------------------------

type HistoryEntry = {
  leftCorners: Corners;
  rightCorners: Corners;
  fineWarpPoints: FineWarpPoint[];
  warpSlices: WarpSlice[];
  split: SplitLine;
  editMode: EditMode;
};

function snapshot(s: {
  leftCorners: Corners;
  rightCorners: Corners;
  fineWarpPoints: FineWarpPoint[];
  warpSlices: WarpSlice[];
  split: SplitLine;
  editMode: EditMode;
}): HistoryEntry {
  return {
    leftCorners: s.leftCorners,
    rightCorners: s.rightCorners,
    fineWarpPoints: s.fineWarpPoints,
    warpSlices: s.warpSlices,
    split: s.split,
    editMode: s.editMode,
  };
}

/** Stable hash for dirty detection (CALIBRATION_UX §6.7 구현 노트). */
function stateHash(s: {
  leftCorners: Corners;
  rightCorners: Corners;
  fineWarpPoints: FineWarpPoint[];
  warpSlices: WarpSlice[];
  maskPolygons?: MaskPolygon[];
  bezierPatches?: BezierPatch[];
  split: SplitLine;
}): string {
  return JSON.stringify({
    L: s.leftCorners,
    R: s.rightCorners,
    F: s.fineWarpPoints,
    W: s.warpSlices,
    M: s.maskPolygons ?? [],
    B: s.bezierPatches ?? [],
    S: s.split,
  });
}

// -----------------------------------------------------------------------------
// Store shape
// -----------------------------------------------------------------------------

export type PendingLockTransition = {
  to: SplitLockMode;
  reason: string;
  willLose: string;
};

export interface CalibrationState {
  // Destination (warp corners in shared global coord space)
  editMode: EditMode;
  leftCorners: Corners;
  rightCorners: Corners;
  fineWarpPoints: FineWarpPoint[];
  warpSlices: WarpSlice[];
  /** NBL 46 — polygon masks (blackout/crop). */
  maskPolygons: MaskPolygon[];
  /** NBL 78 — bézier surface patches (per side). */
  bezierPatches: BezierPatch[];
  /** Which kind of point a double-click adds in the canvas. */
  warpAddMode: 'morph' | 'slice';

  // Source split
  split: SplitLine;

  // Destination seam — convenience helper. topX/bottomX are derived from
  // inner corners (avg of Left.TR/Right.TL and Left.BR/Right.BL). lockMode
  // is session-local UI preference (not persisted). Changes write to the
  // four inner corners' x values.
  destSeamLockMode: SplitLockMode;
  pendingDestSeamLockTransition: PendingLockTransition | null;

  // Playback source (AppState와 동기화)
  playbackSource: PlaybackSource;
  currentMedia: CurrentMedia | null;
  recentMedia: RecentMedia[];

  // Media library (server-backed)
  mediaList: MediaItem[];
  mediaError: string | null;

  // Transport
  playbackState: 'playing' | 'paused' | 'stopped';
  playbackMode: 'schedule' | 'manual';

  // Selection / nudge
  selection: Selection;
  nudgeStep: NudgeStep;
  coordUnit: CoordUnit;

  // Visualization
  testPattern: TestPattern;
  patternMode: PatternMode;
  heatmapEnabled: boolean;
  /**
   * Show a thick high-contrast guide along the inner seam (left wall TR↔BR
   * = right wall TL↔BL) on the unified canvas. Operators want this ON when
   * aligning corners to the physical wall fold; OFF for a clean preview
   * after calibration.
   */
  seamGuideEnabled: boolean;

  /**
   * Active surface template (NBL_REQ_309). Defaults to 'corner' to preserve
   * the legacy left/right 2-wall layout for existing installs/presets.
   */
  surfaceTemplate: SurfaceTemplate;

  // Session flags
  isDragging: boolean;
  browseModalOpen: boolean;
  helpOverlayOpen: boolean;

  // Save state (§6.7)
  activePresetId: string | null;
  lastSavedHash: string;
  liveWarpSyncReady: boolean;

  // Presets (server-backed)
  presets: WarpPreset[];
  presetError: string | null;

  // Server connection
  connectionStatus: ConnectionStatus;

  // Pending lock mode change (confirm dialog gate)
  pendingLockTransition: PendingLockTransition | null;

  // Undo/redo history
  history: HistoryEntry[];
  historyCursor: number;

  // -----------------------------------------------------------------------
  // Actions
  // -----------------------------------------------------------------------

  setEditMode: (m: EditMode) => void;
  selectCorner: (wall: WallSide, index: CornerIndex) => void;
  selectSplit: (position: 'top' | 'bottom') => void;
  selectFineWarpPoint: (id: string) => void;
  selectWarpSlice: (id: string, end: 'seam' | 'outer') => void;
  clearSelection: () => void;

  moveCorner: (wall: WallSide, index: CornerIndex, point: Point) => void;
  addFineWarpPoint: (side: WallSide, source: Point, dest: Point) => void;
  moveFineWarpPoint: (id: string, dest: Point) => void;
  deleteFineWarpPoint: (id: string) => void;
  clearFineWarpPoints: () => void;
  setWarpAddMode: (mode: 'morph' | 'slice') => void;
  addWarpSlice: (
    side: WallSide,
    sourceY: number,
    seamDestY: number,
    outerDestY: number,
  ) => void;
  moveWarpSlice: (id: string, end: 'seam' | 'outer', destY: number) => void;
  deleteWarpSlice: (id: string) => void;
  clearWarpSlices: () => void;
  nudgeSelected: (dx: number, dy: number) => void;

  // NBL 46 — masking
  addMaskPolygon: (points: Point[], invert?: boolean) => void;
  deleteMaskPolygon: (id: string) => void;
  clearMaskPolygons: () => void;
  // NBL 78 — bézier surface
  addBezierPatch: (side: WallSide) => void;
  moveBezierControlPoint: (id: string, index: number, point: Point) => void;
  deleteBezierPatch: (id: string) => void;
  clearBezierPatches: () => void;

  setSplitHandle: (position: 'top' | 'bottom', x: number) => void;
  requestLockModeChange: (to: SplitLockMode) => void;
  confirmLockModeChange: () => void;
  cancelLockModeChange: () => void;

  // Destination seam — writes to inner corner x values
  setDestSeamHandle: (position: 'top' | 'bottom', x: number) => void;
  requestDestSeamLockModeChange: (to: SplitLockMode) => void;
  confirmDestSeamLockModeChange: () => void;
  cancelDestSeamLockModeChange: () => void;

  setPlaybackSource: (src: PlaybackSource) => void;
  setCurrentMedia: (m: CurrentMedia | null) => void;
  setRecentMedia: (r: RecentMedia[]) => void;

  // Media library actions
  loadMedia: () => Promise<void>;
  uploadMedia: (file: File, onProgress?: (fraction: number) => void, options?: { ownerGroupId?: string | null }) => Promise<MediaItem>;
  deleteMedia: (id: string) => Promise<void>;
  updateMediaMeta: (
    id: string,
    patch: { originalName?: string; folderId?: string | null },
  ) => Promise<void>;
  playMedia: (id: string) => Promise<void>;
  playNotice: (id: string) => Promise<void>;
  clearMediaError: () => void;
  receiveMediaChange: (payload: MediaChangePayload) => void;
  receiveMediaProgress: (progress: MediaTranscodeProgress) => void;

  // Transport actions
  play: () => Promise<void>;
  pause: () => Promise<void>;
  stop: () => Promise<void>;
  next: () => Promise<void>;
  prev: () => Promise<void>;
  setPlaybackState: (s: 'playing' | 'paused' | 'stopped') => void;
  setScheduleMode: () => Promise<void>;
  setPlaybackMode: (m: 'schedule' | 'manual') => void;

  setTestPattern: (p: TestPattern) => void;
  setPatternMode: (m: PatternMode) => void;
  setHeatmapEnabled: (b: boolean) => void;
  setSeamGuideEnabled: (b: boolean) => void;
  setSurfaceTemplate: (t: SurfaceTemplate) => void;
  setCoordUnit: (u: CoordUnit) => void;
  setNudgeStep: (n: NudgeStep) => void;
  setBrowseModalOpen: (b: boolean) => void;
  setHelpOverlayOpen: (b: boolean) => void;

  /** Drag lifecycle — history는 드래그 종료 시에만 한 번 기록 */
  beginDrag: () => void;
  endDrag: () => void;

  // Persistence
  loadPreset: (p: WarpPreset) => void;
  markSaved: (presetId: string) => void;
  resetToDefault: () => void;
  revertToSaved: () => void;

  // Server-backed preset CRUD
  setConnectionStatus: (s: ConnectionStatus) => void;
  loadPresets: () => Promise<WarpPreset[]>;
  hydrateLiveWarp: (input: {
    activePresetId: string | null;
    leftCorners: Corners;
    rightCorners: Corners;
    fineWarpPoints: FineWarpPoint[];
    warpSlices: WarpSlice[];
    maskPolygons?: MaskPolygon[];
    bezierPatches?: BezierPatch[];
    split: SplitLine;
  }) => void;
  setLiveWarpSyncReady: (ready: boolean) => void;
  saveOverwrite: () => Promise<WarpPreset | null>;
  saveAs: (name: string) => Promise<WarpPreset>;
  applyPresetById: (id: string) => Promise<void>;
  duplicatePresetById: (id: string) => Promise<void>;
  deletePresetById: (id: string) => Promise<void>;
  togglePresetProtected: (id: string) => Promise<void>;
  /** Download every preset as a JSON file the user can keep as backup. */
  exportPresets: () => Promise<void>;
  /** Read a previously-exported JSON file and add its presets. Returns the
   *  number of presets imported, or rejects with a user-readable error. */
  importPresets: (file: File) => Promise<number>;
  receivePresetSaved: (p: WarpPreset) => void;
  receivePresetDeleted: (id: string) => void;
  clearPresetError: () => void;

  // Undo/Redo
  undo: () => void;
  redo: () => void;
}

// -----------------------------------------------------------------------------
// Initial derived helpers
// -----------------------------------------------------------------------------

const INITIAL_CORE = {
  leftCorners: INITIAL_LEFT,
  rightCorners: INITIAL_RIGHT,
  fineWarpPoints: [] as FineWarpPoint[],
  warpSlices: [] as WarpSlice[],
  split: SPLIT_DEFAULT,
  editMode: 'symmetric' as EditMode,
};

const INITIAL_HASH = stateHash(INITIAL_CORE);

// -----------------------------------------------------------------------------
// Store
// -----------------------------------------------------------------------------

export const useCalibrationStore = create<CalibrationState>((set, get) => ({
  // -------- Destination --------
  editMode: INITIAL_CORE.editMode,
  leftCorners: INITIAL_CORE.leftCorners,
  rightCorners: INITIAL_CORE.rightCorners,
  fineWarpPoints: INITIAL_CORE.fineWarpPoints,
  warpSlices: INITIAL_CORE.warpSlices,
  maskPolygons: [],
  bezierPatches: [],
  warpAddMode: 'morph',

  // -------- Source split --------
  split: INITIAL_CORE.split,

  // -------- Destination seam --------
  destSeamLockMode: 'center',
  pendingDestSeamLockTransition: null,

  // -------- Playback --------
  playbackSource: { kind: 'playlist', playlistId: 'default' },
  currentMedia: null,
  recentMedia: [],

  mediaList: [],
  mediaError: null,

  playbackState: 'stopped',
  playbackMode: 'schedule',

  // -------- Selection / nudge --------
  selection: null,
  nudgeStep: 1,
  coordUnit: 'normalized',

  // -------- Visualization --------
  testPattern: 'checker',
  patternMode: 'replace',
  heatmapEnabled: false,
  seamGuideEnabled: true,
  // Default to the legacy corner layout so existing presets are unaffected.
  surfaceTemplate: 'corner',

  // -------- Session --------
  isDragging: false,
  browseModalOpen: false,
  helpOverlayOpen: false,

  // -------- Save --------
  activePresetId: null,
  lastSavedHash: INITIAL_HASH,
  liveWarpSyncReady: false,
  presets: [],
  presetError: null,
  connectionStatus: 'connecting',
  pendingLockTransition: null,

  // -------- History --------
  history: [snapshot(INITIAL_CORE)],
  historyCursor: 0,

  // -------- Actions --------
  setEditMode: (editMode) => set((s) => commit(s, { editMode })),

  selectCorner: (wall, index) =>
    set({ selection: { kind: 'corner', wall, index } }),
  selectSplit: (position) =>
    set({ selection: { kind: 'split', position } }),
  selectFineWarpPoint: (id) =>
    set({ selection: { kind: 'fineWarp', id } }),
  clearSelection: () => set({ selection: null }),

  moveCorner: (wall, index, point) =>
    set((s) => commit(s, moveCornerPatch(s, wall, index, point))),

  addFineWarpPoint: (side, source, dest) =>
    set((s) => {
      const id = `fw-${Date.now().toString(36)}-${s.fineWarpPoints.length}`;
      return commit(s, {
        fineWarpPoints: [
          ...s.fineWarpPoints,
          { id, side, source, dest, radius: 0.16 },
        ],
        selection: { kind: 'fineWarp', id },
      });
    }),

  moveFineWarpPoint: (id, dest) =>
    set((s) => commit(s, {
      fineWarpPoints: s.fineWarpPoints.map((p) =>
        p.id === id ? { ...p, dest } : p,
      ),
      selection: { kind: 'fineWarp', id },
    })),

  deleteFineWarpPoint: (id) =>
    set((s) => commit(s, {
      fineWarpPoints: s.fineWarpPoints.filter((p) => p.id !== id),
      selection: s.selection?.kind === 'fineWarp' && s.selection.id === id
        ? null
        : s.selection,
    })),

  clearFineWarpPoints: () =>
    set((s) => commit(s, { fineWarpPoints: [], selection: null })),

  setWarpAddMode: (mode) => set({ warpAddMode: mode }),

  selectWarpSlice: (id, end) =>
    set({ selection: { kind: 'warpSlice', id, end } }),

  addWarpSlice: (side, sourceY, seamDestY, outerDestY) =>
    set((s) => {
      const id = `ws-${Date.now().toString(36)}-${s.warpSlices.length}`;
      return commit(s, {
        warpSlices: [
          ...s.warpSlices,
          { id, side, sourceY, seamDestY, outerDestY },
        ],
        // Select the outer endpoint by default so it's ready to fine-tune.
        selection: { kind: 'warpSlice', id, end: 'outer' },
      });
    }),

  moveWarpSlice: (id, end, destY) =>
    set((s) => commit(s, {
      warpSlices: s.warpSlices.map((sl) =>
        sl.id === id
          ? { ...sl, ...(end === 'seam' ? { seamDestY: destY } : { outerDestY: destY }) }
          : sl,
      ),
      selection: { kind: 'warpSlice', id, end },
    })),

  deleteWarpSlice: (id) =>
    set((s) => commit(s, {
      warpSlices: s.warpSlices.filter((sl) => sl.id !== id),
      selection: s.selection?.kind === 'warpSlice' && s.selection.id === id
        ? null
        : s.selection,
    })),

  clearWarpSlices: () =>
    set((s) => commit(s, { warpSlices: [], selection: null })),

  // -------- NBL 46 — masking --------
  addMaskPolygon: (points, invert) =>
    set((s) => {
      const id = `mask-${Date.now().toString(36)}-${s.maskPolygons.length}`;
      const mask: MaskPolygon = invert ? { id, points, invert } : { id, points };
      return { maskPolygons: [...s.maskPolygons, mask] };
    }),
  deleteMaskPolygon: (id) =>
    set((s) => ({ maskPolygons: s.maskPolygons.filter((m) => m.id !== id) })),
  clearMaskPolygons: () => set({ maskPolygons: [] }),

  // -------- NBL 78 — bézier surface --------
  addBezierPatch: (side) =>
    set((s) => {
      const id = `bez-${Date.now().toString(36)}-${s.bezierPatches.length}`;
      // New patches start as the identity grid (zero displacement) so adding
      // one is a no-op until the operator bends a control point.
      return {
        bezierPatches: [
          ...s.bezierPatches,
          { id, side, controlPoints: identityControlPoints() },
        ],
      };
    }),
  moveBezierControlPoint: (id, index, point) =>
    set((s) => ({
      bezierPatches: s.bezierPatches.map((b) =>
        b.id === id
          ? {
              ...b,
              controlPoints: b.controlPoints.map((cp, i) =>
                i === index ? point : cp,
              ),
            }
          : b,
      ),
    })),
  deleteBezierPatch: (id) =>
    set((s) => ({ bezierPatches: s.bezierPatches.filter((b) => b.id !== id) })),
  clearBezierPatches: () => set({ bezierPatches: [] }),

  nudgeSelected: (dx, dy) =>
    set((s) => {
      if (!s.selection) return {};
      if (s.selection.kind === 'corner') {
        const { wall, index } = s.selection;
        const cur = wall === 'left' ? s.leftCorners[index] : s.rightCorners[index];
        const next: Point = { x: cur.x + dx, y: cur.y + dy };
        return commit(s, moveCornerPatch(s, wall, index, next));
      }
      if (s.selection.kind === 'fineWarp') {
        const { id } = s.selection;
        return commit(s, {
          fineWarpPoints: s.fineWarpPoints.map((p) =>
            p.id === id
              ? { ...p, dest: { x: p.dest.x + dx, y: p.dest.y + dy } }
              : p,
          ),
        });
      }
      if (s.selection.kind === 'warpSlice') {
        // Slice endpoints nudge vertically only.
        const { id, end } = s.selection;
        return commit(s, {
          warpSlices: s.warpSlices.map((sl) =>
            sl.id === id
              ? {
                  ...sl,
                  ...(end === 'seam'
                    ? { seamDestY: sl.seamDestY + dy }
                    : { outerDestY: sl.outerDestY + dy }),
                }
              : sl,
          ),
        });
      }
      // split handle nudge: only x matters
      const pos = s.selection.position;
      const curX = pos === 'top' ? s.split.topX : s.split.bottomX;
      const nextSplit = applySplitDrag(s.split, pos, curX + dx);
      return commit(s, { split: nextSplit });
    }),

  setSplitHandle: (position, x) =>
    set((s) => commit(s, { split: applySplitDrag(s.split, position, x) })),

  requestLockModeChange: (to) =>
    set((s) => {
      const t = transitionLockMode(s.split, to);
      if (!t.needsConfirm) {
        return commit(s, { split: t.next });
      }
      return {
        pendingLockTransition: {
          to,
          reason: t.reason ?? '',
          willLose: t.willLose ?? '',
        },
      };
    }),

  confirmLockModeChange: () =>
    set((s) => {
      if (!s.pendingLockTransition) return {};
      const t = transitionLockMode(s.split, s.pendingLockTransition.to);
      return commit(s, {
        split: t.next,
        pendingLockTransition: null,
      });
    }),

  cancelLockModeChange: () => set({ pendingLockTransition: null }),

  setDestSeamHandle: (position, x) =>
    set((s) => {
      // In symmetric mode the seam is pinned to the mirror axis (x=0.5).
      if (s.editMode === 'symmetric') return {};
      const current = deriveDestSeam(s);
      const next = applySplitDrag(current, position, x);
      if (next === current) return {};
      return commit(s, applyDestSeamToCorners(s, next));
    }),

  requestDestSeamLockModeChange: (to) =>
    set((s) => {
      const current = deriveDestSeam(s);
      const t = transitionLockMode(current, to);
      if (!t.needsConfirm) {
        return commit(s, {
          destSeamLockMode: to,
          ...applyDestSeamToCorners(s, t.next),
        });
      }
      return {
        pendingDestSeamLockTransition: {
          to,
          reason: t.reason ?? '',
          willLose: t.willLose ?? '',
        },
      };
    }),

  confirmDestSeamLockModeChange: () =>
    set((s) => {
      if (!s.pendingDestSeamLockTransition) return {};
      const current = deriveDestSeam(s);
      const t = transitionLockMode(current, s.pendingDestSeamLockTransition.to);
      return commit(s, {
        destSeamLockMode: s.pendingDestSeamLockTransition.to,
        pendingDestSeamLockTransition: null,
        ...applyDestSeamToCorners(s, t.next),
      });
    }),

  cancelDestSeamLockModeChange: () =>
    set({ pendingDestSeamLockTransition: null }),

  setPlaybackSource: (src) => set({ playbackSource: src }),
  setCurrentMedia: (currentMedia) => set({ currentMedia }),
  setRecentMedia: (recentMedia) => set({ recentMedia }),

  loadMedia: async () => {
    try {
      const [list, recent] = await Promise.all([api.listMedia(), api.recentMedia(10)]);
      set({
        mediaList: list,
        recentMedia: recent.map((m) => ({ id: m.id, filename: m.originalName })),
        mediaError: null,
      });
    } catch (e) {
      set({ mediaError: (e as Error).message });
    }
  },
  uploadMedia: async (file, onProgress, options) => {
    try {
      const item = await api.uploadMedia(file, onProgress, options);
      // Optimistic update; WS media:change will also arrive.
      set((s) => ({
        mediaList: [item, ...s.mediaList.filter((m) => m.id !== item.id)],
        mediaError: null,
      }));
      return item;
    } catch (e) {
      set({ mediaError: (e as Error).message });
      throw e;
    }
  },
  deleteMedia: async (id) => {
    try {
      await api.deleteMedia(id);
      set((s) => ({
        mediaList: s.mediaList.filter((m) => m.id !== id),
        recentMedia: s.recentMedia.filter((m) => m.id !== id),
        currentMedia:
          s.playbackSource.kind === 'single-media' && s.playbackSource.mediaId === id
            ? null
            : s.currentMedia,
      }));
    } catch (e) {
      set({ mediaError: (e as Error).message });
      throw e;
    }
  },
  updateMediaMeta: async (id, patch) => {
    try {
      const updated = await api.updateMediaMeta(id, patch);
      set((s) => ({
        mediaList: s.mediaList.map((m) => (m.id === id ? updated : m)),
      }));
    } catch (e) {
      set({ mediaError: (e as Error).message });
      throw e;
    }
  },
  playMedia: async (id) => {
    try {
      await api.playMedia(id);
      const item = get().mediaList.find((m) => m.id === id);
      set({
        playbackSource: { kind: 'single-media', mediaId: id },
        playbackState: 'paused',
        currentMedia: item
          ? {
              filename: item.originalName,
              durationSec: item.durationSec,
              positionSec: 0,
              type: item.mediaType,
              isPlaying: false,
            }
          : null,
      });
    } catch (e) {
      set({ mediaError: (e as Error).message });
      throw e;
    }
  },
  playNotice: async (id) => {
    try {
      await api.playNotice(id);
      set({
        playbackSource: { kind: 'single-notice', noticeId: id },
        playbackState: 'playing',
        playbackMode: 'manual',
      });
    } catch (e) {
      set({ mediaError: (e as Error).message });
      throw e;
    }
  },
  clearMediaError: () => set({ mediaError: null }),

  // Transport
  setPlaybackState: (playbackState) =>
    set((s) => ({
      playbackState,
      currentMedia: s.currentMedia
        ? { ...s.currentMedia, isPlaying: playbackState === 'playing' }
        : s.currentMedia,
    })),
  play: async () => {
    try {
      await api.playback.play();
      set((s) => ({
        playbackState: 'playing',
        currentMedia: s.currentMedia ? { ...s.currentMedia, isPlaying: true } : s.currentMedia,
      }));
    } catch (e) {
      set({ mediaError: (e as Error).message });
    }
  },
  pause: async () => {
    try {
      await api.playback.pause();
      set((s) => ({
        playbackState: 'paused',
        currentMedia: s.currentMedia ? { ...s.currentMedia, isPlaying: false } : s.currentMedia,
      }));
    } catch (e) {
      set({ mediaError: (e as Error).message });
    }
  },
  stop: async () => {
    try {
      await api.playback.stop();
      set((s) => ({
        playbackState: 'stopped',
        currentMedia: s.currentMedia ? { ...s.currentMedia, isPlaying: false } : s.currentMedia,
      }));
    } catch (e) {
      set({ mediaError: (e as Error).message });
    }
  },
  next: async () => {
    try {
      await api.playback.next();
    } catch (e) {
      set({ mediaError: (e as Error).message });
    }
  },
  prev: async () => {
    try {
      await api.playback.prev();
    } catch (e) {
      set({ mediaError: (e as Error).message });
    }
  },
  setPlaybackMode: (playbackMode) => set({ playbackMode }),
  setScheduleMode: async () => {
    try {
      await api.playback.setMode('schedule');
      set({ playbackMode: 'schedule' });
    } catch (e) {
      set({ mediaError: (e as Error).message });
    }
  },
  receiveMediaChange: (payload) =>
    set((s) => {
      if (payload.kind === 'created') {
        if (s.mediaList.some((m) => m.id === payload.item.id)) return {};
        return { mediaList: [payload.item, ...s.mediaList] };
      }
      if (payload.kind === 'updated') {
        // 서버 변환 상태·변환본 갱신. 권한 표시(canWrite 등)는 목록 응답에만 있으니 기존 값 위에 덮는다.
        if (!s.mediaList.some((m) => m.id === payload.item.id)) return {};
        return { mediaList: s.mediaList.map((m) => {
          if (m.id !== payload.item.id) return m;
          const { transcodeProgress, ...rest } = m;
          const next = { ...rest, ...payload.item };
          return next.processingStatus === 'processing' && transcodeProgress ? { ...next, transcodeProgress } : next;
        }) };
      }
      if (payload.kind === 'deleted') {
        return {
          mediaList: s.mediaList.filter((m) => m.id !== payload.id),
          recentMedia: s.recentMedia.filter((m) => m.id !== payload.id),
        };
      }
      // 'played' — move to front of recentMedia
      const played = s.mediaList.find((m) => m.id === payload.id);
      if (!played) return {};
      const entry: RecentMedia = { id: played.id, filename: played.originalName };
      return {
        recentMedia: [entry, ...s.recentMedia.filter((m) => m.id !== played.id)].slice(0, 10),
      };
    }),

  receiveMediaProgress: (progress) =>
    set((s) => s.mediaList.some((m) => m.id === progress.id)
      ? { mediaList: s.mediaList.map((m) => m.id === progress.id ? { ...m, processingStatus: 'processing', transcodeProgress: progress } : m) }
      : {}),

  setTestPattern: (testPattern) => {
    set({ testPattern });
    // Fire-and-forget broadcast to Player. Errors are non-fatal for Admin's
    // own canvas preview — it uses the local state regardless.
    void api.playback.setTestPattern(testPattern).catch(() => {});
  },
  setPatternMode: (patternMode) => set({ patternMode }),
  setHeatmapEnabled: (heatmapEnabled) => set({ heatmapEnabled }),
  setSeamGuideEnabled: (seamGuideEnabled) => {
    set({ seamGuideEnabled });
    // Mirror to the Player via server broadcast. Errors are non-fatal —
    // Admin already updated locally and the next toggle will retry.
    void api.playback.setSeamGuide(seamGuideEnabled).catch(() => undefined);
  },
  setSurfaceTemplate: (surfaceTemplate) => {
    // Switching to a single flat plane has no inner seam to align — turn the
    // seam guide off so the preview is clean. Corner restores it.
    if (surfaceTemplate === 'single') {
      set({ surfaceTemplate, seamGuideEnabled: false });
      void api.playback.setSeamGuide(false).catch(() => undefined);
    } else {
      set({ surfaceTemplate });
    }
  },
  setCoordUnit: (coordUnit) => set({ coordUnit }),
  setNudgeStep: (nudgeStep) => set({ nudgeStep }),
  setBrowseModalOpen: (browseModalOpen) => set({ browseModalOpen }),
  setHelpOverlayOpen: (helpOverlayOpen) => set({ helpOverlayOpen }),

  beginDrag: () => set({ isDragging: true }),
  endDrag: () =>
    set((s) => {
      if (!s.isDragging) return {};
      // Push a single history entry for the whole drag gesture.
      return { isDragging: false, ...pushHistoryPatch(s) };
    }),

  // -------- Persistence --------
  loadPreset: (p) =>
    set((s) => {
      const core = {
        leftCorners: p.leftCorners,
        rightCorners: p.rightCorners,
        fineWarpPoints: p.fineWarpPoints,
        warpSlices: p.warpSlices,
        split: {
          topX: p.splitTopX,
          bottomX: p.splitBottomX,
          lockMode: p.splitLockMode,
        },
        editMode: (p.symmetric ? 'symmetric' : 'free') as EditMode,
      };
      return {
        ...core,
        maskPolygons: p.maskPolygons ?? [],
        bezierPatches: p.bezierPatches ?? [],
        activePresetId: p.id,
        lastSavedHash: stateHash({
          ...core,
          maskPolygons: p.maskPolygons ?? [],
          bezierPatches: p.bezierPatches ?? [],
        }),
        selection: null,
        destSeamLockMode: inferDestSeamLockMode(p.leftCorners, p.rightCorners),
        pendingDestSeamLockTransition: null,
        history: [snapshot({ ...s, ...core })],
        historyCursor: 0,
      };
    }),

  markSaved: (presetId) =>
    set((s) => ({
      activePresetId: presetId,
      lastSavedHash: stateHash(s),
    })),

  resetToDefault: () =>
    set((s) =>
      commit(s, {
        ...INITIAL_CORE,
        selection: null,
      }),
    ),

  revertToSaved: () =>
    set((s) => {
      const entry = s.history.find((e) => stateHash(e) === s.lastSavedHash);
      if (!entry) return {};
      return commit(s, {
        leftCorners: entry.leftCorners,
        rightCorners: entry.rightCorners,
        fineWarpPoints: entry.fineWarpPoints,
        warpSlices: entry.warpSlices,
        split: entry.split,
        editMode: entry.editMode,
      });
    }),

  // -------- Server-backed preset CRUD --------
  setConnectionStatus: (connectionStatus) => set({ connectionStatus }),

  loadPresets: async () => {
    try {
      const presets = await api.listPresets();
      set({ presets, presetError: null });
      return presets;
    } catch (e) {
      set({ presetError: (e as Error).message });
      return [];
    }
  },

  hydrateLiveWarp: (input) =>
    set((s) => {
      const core = {
        leftCorners: input.leftCorners,
        rightCorners: input.rightCorners,
        fineWarpPoints: input.fineWarpPoints,
        warpSlices: input.warpSlices,
        split: input.split,
        editMode: s.editMode,
      };
      const maskPolygons = input.maskPolygons ?? [];
      const bezierPatches = input.bezierPatches ?? [];
      return {
        ...core,
        maskPolygons,
        bezierPatches,
        activePresetId: input.activePresetId,
        lastSavedHash: stateHash({ ...core, maskPolygons, bezierPatches }),
        selection: null,
        destSeamLockMode: inferDestSeamLockMode(input.leftCorners, input.rightCorners),
        pendingDestSeamLockTransition: null,
        history: [snapshot(core)],
        historyCursor: 0,
      };
    }),

  setLiveWarpSyncReady: (liveWarpSyncReady) => set({ liveWarpSyncReady }),

  saveOverwrite: async () => {
    const s = get();
    if (!s.activePresetId) {
      // No active preset → delegate to saveAs with a default name.
      return null;
    }
    try {
      const updated = await api.updatePreset(s.activePresetId, {
        leftCorners: s.leftCorners,
        rightCorners: s.rightCorners,
        fineWarpPoints: s.fineWarpPoints,
        warpSlices: s.warpSlices,
        maskPolygons: s.maskPolygons,
        bezierPatches: s.bezierPatches,
        splitTopX: s.split.topX,
        splitBottomX: s.split.bottomX,
        splitLockMode: s.split.lockMode,
        symmetric: s.editMode === 'symmetric',
      });
      set((st) => ({
        lastSavedHash: stateHash(st),
        presets: st.presets.map((p) => (p.id === updated.id ? updated : p)),
        presetError: null,
      }));
      return updated;
    } catch (e) {
      set({ presetError: (e as Error).message });
      return null;
    }
  },

  saveAs: async (name: string) => {
    const s = get();
    const input: NewPresetInput = {
      name,
      leftCorners: s.leftCorners,
      rightCorners: s.rightCorners,
      fineWarpPoints: s.fineWarpPoints,
      warpSlices: s.warpSlices,
      maskPolygons: s.maskPolygons,
      bezierPatches: s.bezierPatches,
      splitTopX: s.split.topX,
      splitBottomX: s.split.bottomX,
      splitLockMode: s.split.lockMode,
      symmetric: s.editMode === 'symmetric',
      bypassWarp: false,
      outputResolutionW: 3840,
      outputResolutionH: 2160,
      protected: false,
    };
    try {
      const created = await api.createPreset(input);
      set((st) => ({
        activePresetId: created.id,
        lastSavedHash: stateHash(st),
        presets: [created, ...st.presets.filter((p) => p.id !== created.id)],
        presetError: null,
      }));
      return created;
    } catch (e) {
      set({ presetError: (e as Error).message });
      throw e;
    }
  },

  applyPresetById: async (id) => {
    try {
      const preset = await api.applyPreset(id);
      // Hydrate local state from the applied preset
      get().loadPreset(preset);
    } catch (e) {
      set({ presetError: (e as Error).message });
    }
  },

  duplicatePresetById: async (id) => {
    try {
      const copy = await api.duplicatePreset(id);
      set((st) => ({
        presets: [copy, ...st.presets.filter((p) => p.id !== copy.id)],
        presetError: null,
      }));
    } catch (e) {
      set({ presetError: (e as Error).message });
    }
  },

  deletePresetById: async (id) => {
    try {
      await api.deletePreset(id);
      set((st) => ({
        presets: st.presets.filter((p) => p.id !== id),
        activePresetId:
          st.activePresetId === id ? null : st.activePresetId,
        presetError: null,
      }));
    } catch (e) {
      set({ presetError: (e as Error).message });
    }
  },

  togglePresetProtected: async (id) => {
    const current = get().presets.find((p) => p.id === id);
    if (!current) return;
    try {
      const updated = await api.setPresetProtected(id, !current.protected);
      set((st) => ({
        presets: st.presets.map((p) => (p.id === id ? updated : p)),
        presetError: null,
      }));
    } catch (e) {
      set({ presetError: (e as Error).message });
    }
  },

  exportPresets: async () => {
    try {
      const file = await api.exportPresets();
      const blob = new Blob([JSON.stringify(file, null, 2)], {
        type: 'application/json',
      });
      const url = URL.createObjectURL(blob);
      const stamp = new Date()
        .toISOString()
        .replace(/[:.]/g, '-')
        .slice(0, 19);
      const a = document.createElement('a');
      a.href = url;
      a.download = `warp-presets-${stamp}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Defer revoke so the browser has a chance to start the download.
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      set({ presetError: null });
    } catch (e) {
      set({ presetError: `Export failed: ${(e as Error).message}` });
    }
  },

  importPresets: async (file: File) => {
    let parsed: unknown;
    try {
      const text = await file.text();
      parsed = JSON.parse(text);
    } catch (e) {
      const msg = `Import failed: ${(e as Error).message}`;
      set({ presetError: msg });
      throw new Error(msg);
    }
    if (
      !parsed ||
      typeof parsed !== 'object' ||
      (parsed as { schemaVersion?: unknown }).schemaVersion !== 1 ||
      !Array.isArray((parsed as { presets?: unknown }).presets)
    ) {
      const msg = 'Import failed: file does not look like a warp-preset export';
      set({ presetError: msg });
      throw new Error(msg);
    }
    try {
      const { imported } = await api.importPresets(
        parsed as Parameters<typeof api.importPresets>[0],
      );
      set((st) => {
        const existingIds = new Set(st.presets.map((p) => p.id));
        const fresh = imported.filter((p) => !existingIds.has(p.id));
        return {
          presets: [...fresh, ...st.presets],
          presetError: null,
        };
      });
      return imported.length;
    } catch (e) {
      const msg = `Import failed: ${(e as Error).message}`;
      set({ presetError: msg });
      throw new Error(msg);
    }
  },

  receivePresetSaved: (p) =>
    set((st) => {
      const existing = st.presets.some((x) => x.id === p.id);
      const presets = existing
        ? st.presets.map((x) => (x.id === p.id ? p : x))
        : [p, ...st.presets];
      return { presets };
    }),

  receivePresetDeleted: (id) =>
    set((st) => ({
      presets: st.presets.filter((p) => p.id !== id),
      activePresetId: st.activePresetId === id ? null : st.activePresetId,
    })),

  clearPresetError: () => set({ presetError: null }),

  // -------- Undo / Redo --------
  undo: () =>
    set((s) => {
      if (s.historyCursor <= 0) return {};
      const nextCursor = s.historyCursor - 1;
      const entry = s.history[nextCursor];
      if (!entry) return {};
      return {
        leftCorners: entry.leftCorners,
        rightCorners: entry.rightCorners,
        fineWarpPoints: entry.fineWarpPoints,
        warpSlices: entry.warpSlices,
        split: entry.split,
        editMode: entry.editMode,
        historyCursor: nextCursor,
      };
    }),

  redo: () =>
    set((s) => {
      if (s.historyCursor >= s.history.length - 1) return {};
      const nextCursor = s.historyCursor + 1;
      const entry = s.history[nextCursor];
      if (!entry) return {};
      return {
        leftCorners: entry.leftCorners,
        rightCorners: entry.rightCorners,
        fineWarpPoints: entry.fineWarpPoints,
        warpSlices: entry.warpSlices,
        split: entry.split,
        editMode: entry.editMode,
        historyCursor: nextCursor,
      };
    }),
}));

// -----------------------------------------------------------------------------
// Helpers that operate on state slices
// -----------------------------------------------------------------------------

/** Apply a core (corners/split/editMode) patch, record history unless dragging. */
function commit(
  s: CalibrationState,
  patch: Partial<
    Pick<CalibrationState, 'leftCorners' | 'rightCorners' | 'fineWarpPoints' | 'split' | 'editMode'>
  > &
    Partial<CalibrationState>,
): Partial<CalibrationState> {
  const merged: CalibrationState = { ...s, ...patch } as CalibrationState;
  if (s.isDragging) {
    // History push deferred until endDrag()
    return patch;
  }
  return { ...patch, ...pushHistoryPatch(merged) };
}

function pushHistoryPatch(s: CalibrationState): Pick<
  CalibrationState,
  'history' | 'historyCursor'
> {
  const entry = snapshot(s);
  const base = s.history.slice(0, s.historyCursor + 1);
  base.push(entry);
  const trimmed = base.length > MAX_HISTORY ? base.slice(base.length - MAX_HISTORY) : base;
  return { history: trimmed, historyCursor: trimmed.length - 1 };
}

/** Inner corners (tied to the opposite wall's inner corner as a shared seam). */
function isInnerCorner(wall: WallSide, index: CornerIndex): boolean {
  return (
    (wall === 'left' && (index === 1 || index === 2)) ||
    (wall === 'right' && (index === 0 || index === 3))
  );
}

/** For an inner corner, return the paired (shared) corner on the other wall. */
function innerPair(
  wall: WallSide,
  index: CornerIndex,
): { side: WallSide; index: CornerIndex } {
  if (wall === 'left') {
    // L.TR(1) ↔ R.TL(0), L.BR(2) ↔ R.BL(3)
    return { side: 'right', index: (index === 1 ? 0 : 3) as CornerIndex };
  }
  // R.TL(0) ↔ L.TR(1), R.BL(3) ↔ L.BR(2)
  return { side: 'left', index: (index === 0 ? 1 : 2) as CornerIndex };
}

/**
 * Corner move patch — handles:
 *  1. Inner-corner pair binding (top/bottom seam). Dragging either side
 *     updates BOTH corners to the same point. In 'symmetric' mode the
 *     inner x is clamped to 0.5 (mirror axis).
 *  2. Outer-corner mirroring in 'symmetric' mode (x' = 1 - x, y' = y).
 *  3. Free mode: just updates the target corner.
 */
function moveCornerPatch(
  s: CalibrationState,
  wall: WallSide,
  index: CornerIndex,
  point: Point,
): Pick<CalibrationState, 'leftCorners' | 'rightCorners'> {
  if (isInnerCorner(wall, index)) {
    const p: Point =
      s.editMode === 'symmetric' ? { x: 0.5, y: point.y } : point;
    const pair = innerPair(wall, index);
    let left = s.leftCorners;
    let right = s.rightCorners;
    if (wall === 'left') left = replaceCorner(left, index, p);
    else right = replaceCorner(right, index, p);
    if (pair.side === 'left') left = replaceCorner(left, pair.index, p);
    else right = replaceCorner(right, pair.index, p);
    return { leftCorners: left, rightCorners: right };
  }

  // Outer corner
  if (s.editMode === 'symmetric') {
    const mirrorIdx = MIRROR_MAP[index];
    const mirrored = mirrorPoint(point);
    if (wall === 'left') {
      return {
        leftCorners: replaceCorner(s.leftCorners, index, point),
        rightCorners: replaceCorner(s.rightCorners, mirrorIdx, mirrored),
      };
    }
    return {
      rightCorners: replaceCorner(s.rightCorners, index, point),
      leftCorners: replaceCorner(s.leftCorners, mirrorIdx, mirrored),
    };
  }

  // Free mode, outer corner — update only the targeted side
  if (wall === 'left') {
    return {
      leftCorners: replaceCorner(s.leftCorners, index, point),
      rightCorners: s.rightCorners,
    };
  }
  return {
    leftCorners: s.leftCorners,
    rightCorners: replaceCorner(s.rightCorners, index, point),
  };
}

// =============================================================================
// Derived selectors — Zustand selector 패턴으로 hook 제공.
// 4-point homography는 저렴하므로 매 렌더 재계산. 필요시 useMemo.
// =============================================================================

// -----------------------------------------------------------------------------
// Destination seam helpers — derived from inner corner x values
// -----------------------------------------------------------------------------

const DEST_SEAM_EPS = 1e-4;

/** Derive effective destSeam from current inner corners + session lockMode. */
function deriveDestSeam(s: {
  leftCorners: Corners;
  rightCorners: Corners;
  destSeamLockMode: SplitLockMode;
}): SplitLine {
  const topX =
    (s.leftCorners[1].x + s.rightCorners[0].x) / 2;
  const bottomX =
    (s.leftCorners[2].x + s.rightCorners[3].x) / 2;
  return { topX, bottomX, lockMode: s.destSeamLockMode };
}

/** Overwrite x of Left.TR/Right.TL (top seam) and Left.BR/Right.BL (bottom). */
function applyDestSeamToCorners(
  s: { leftCorners: Corners; rightCorners: Corners },
  seam: SplitLine,
): Pick<CalibrationState, 'leftCorners' | 'rightCorners'> {
  const left = [...s.leftCorners] as [Point, Point, Point, Point];
  const right = [...s.rightCorners] as [Point, Point, Point, Point];
  left[1] = { x: seam.topX, y: left[1].y };
  right[0] = { x: seam.topX, y: right[0].y };
  left[2] = { x: seam.bottomX, y: left[2].y };
  right[3] = { x: seam.bottomX, y: right[3].y };
  return { leftCorners: left, rightCorners: right };
}

/** Best-effort inference: choose the most restrictive lock mode that fits. */
function inferDestSeamLockMode(
  left: Corners,
  right: Corners,
): SplitLockMode {
  const topX = (left[1].x + right[0].x) / 2;
  const bottomX = (left[2].x + right[3].x) / 2;
  if (
    Math.abs(topX - 0.5) < DEST_SEAM_EPS &&
    Math.abs(bottomX - 0.5) < DEST_SEAM_EPS
  ) {
    return 'center';
  }
  if (Math.abs(topX - bottomX) < DEST_SEAM_EPS) return 'parallel';
  return 'free';
}

export const useDestSeam = (): SplitLine =>
  useCalibrationStore((s) => deriveDestSeam(s));

// -----------------------------------------------------------------------------

export const useLeftSourceQuad = (): Corners =>
  useCalibrationStore((s) => leftSourceQuad(s.split));

export const useRightSourceQuad = (): Corners =>
  useCalibrationStore((s) => rightSourceQuad(s.split));

export const useLeftHomography = (): Matrix3x3 =>
  useCalibrationStore((s) =>
    computeHomography(leftSourceQuad(s.split), s.leftCorners),
  );

export const useRightHomography = (): Matrix3x3 =>
  useCalibrationStore((s) =>
    computeHomography(rightSourceQuad(s.split), s.rightCorners),
  );

export const useIsTestSourceActive = (): boolean =>
  useCalibrationStore(
    (s) =>
      s.playbackSource.kind === 'test-procedural' ||
      s.playbackSource.kind === 'test-uploaded',
  );

export const useIsDirty = (): boolean =>
  useCalibrationStore((s) => stateHash(s) !== s.lastSavedHash);

export const useSaveStatus = (): SaveStatus =>
  useCalibrationStore((s) => {
    const dirty = stateHash(s) !== s.lastSavedHash;
    if (!dirty) return 'saved';
    return s.activePresetId !== null ? 'unsaved' : 'new';
  });

export const useCanUndo = (): boolean =>
  useCalibrationStore((s) => s.historyCursor > 0);

export const useCanRedo = (): boolean =>
  useCalibrationStore((s) => s.historyCursor < s.history.length - 1);
